package com.schoolconnect.app.repository.home

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.response.DashboardAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.DashboardSchoolsAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.DashboardStatisticsResponse
import com.schoolconnect.app.datasource.network.response.EventsNoticesAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.SchoolAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.RecentActivitiesResponse
import com.schoolconnect.app.datasource.network.response.SystemHealthResponse
import com.schoolconnect.app.datasource.network.response.TeacherDashboardOverviewResponse
import com.schoolconnect.app.datasource.network.response.ParentListResponse
import com.schoolconnect.app.datasource.network.response.ParentDashboardResponse
import com.schoolconnect.app.datasource.network.response.ParentResponse
import com.schoolconnect.app.datasource.network.response.StudentResponse
import com.schoolconnect.app.datasource.network.response.QuickAnnouncementResponse
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

object HomeDashboardRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] HomeDashboardRepository"
    private const val MAX_MEMORY_CACHE_ENTRIES = 32

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private var dashboardAnalyticsCache = mutableMapOf<String, CachedValue<DashboardAnalyticsResponse>>()
    private var dashboardSchoolsAnalyticsCache = mutableMapOf<String, CachedValue<DashboardSchoolsAnalyticsResponse>>()
    private var dashboardStatisticsCache = mutableMapOf<String, CachedValue<DashboardStatisticsResponse>>()
    private var schoolAnalyticsCache = mutableMapOf<String, CachedValue<SchoolAnalyticsResponse>>()
    private var schoolEventsNoticesCache = mutableMapOf<String, CachedValue<EventsNoticesAnalyticsResponse>>()
    private var recentActivitiesCache = mutableMapOf<String, CachedValue<RecentActivitiesResponse>>()
    private var systemHealthCache = mutableMapOf<String, CachedValue<SystemHealthResponse>>()
    private var teacherDashboardCache = mutableMapOf<String, CachedValue<TeacherDashboardOverviewResponse>>()
    private var parentDashboardCache = mutableMapOf<String, CachedValue<ParentDashboardResponse>>()
    private var parentProfileCache = mutableMapOf<String, CachedValue<ParentListResponse>>()
    private var parentRecordCache = mutableMapOf<String, CachedValue<ParentResponse>>()
    private var parentWardsCache = mutableMapOf<String, CachedValue<List<StudentResponse>>>()
    private var activeAnnouncementsCache = mutableMapOf<String, CachedValue<List<QuickAnnouncementResponse>>>()

    fun peekDashboardAnalytics(roleCode: String): DashboardAnalyticsResponse? =
        dashboardAnalyticsCache[cacheKey(roleCode)]?.value

    fun peekDashboardSchoolsAnalytics(roleCode: String): DashboardSchoolsAnalyticsResponse? =
        com.schoolconnect.app.repository.school.SchoolRepository.peekSchoolsAnalytics()
            ?: dashboardSchoolsAnalyticsCache[cacheKey(roleCode)]?.value

    fun peekDashboardStatistics(roleCode: String): DashboardStatisticsResponse? =
        dashboardStatisticsCache[cacheKey(roleCode)]?.value

    fun peekSchoolAnalytics(schoolCode: String): SchoolAnalyticsResponse? =
        schoolAnalyticsCache[cacheKey(schoolCode)]?.value

    fun peekSchoolEventsNotices(schoolId: String): EventsNoticesAnalyticsResponse? =
        schoolEventsNoticesCache[cacheKey(schoolId)]?.value

    fun peekRecentActivities(roleCode: String): RecentActivitiesResponse? =
        recentActivitiesCache[cacheKey(roleCode)]?.value

    fun peekSystemHealth(roleCode: String): SystemHealthResponse? =
        systemHealthCache[cacheKey(roleCode)]?.value

    fun peekTeacherDashboard(roleCode: String): TeacherDashboardOverviewResponse? =
        teacherDashboardCache[cacheKey(roleCode)]?.value

    fun peekParentDashboard(scopeKey: String): ParentDashboardResponse? = parentDashboardCache[cacheKey(scopeKey)]?.value

    fun peekParentProfile(parentId: String): ParentListResponse? = parentProfileCache[cacheKey(parentId)]?.value

    fun peekParentRecord(parentId: String): ParentResponse? = parentRecordCache[cacheKey(parentId)]?.value

    fun peekParentWards(parentId: String): List<StudentResponse>? = parentWardsCache[cacheKey(parentId)]?.value

    fun peekActiveAnnouncements(schoolId: String): List<QuickAnnouncementResponse>? =
        activeAnnouncementsCache[cacheKey(schoolId)]?.value

    suspend fun loadActiveAnnouncements(schoolId: String, forceRemote: Boolean = false): Result<List<QuickAnnouncementResponse>> {
        val key = cacheKey(schoolId)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadActiveAnnouncements",
            context = "school=${schoolId.ifBlank { "<any>" }}",
            cachedValue = activeAnnouncementsCache[key],
            fetchValue = {
                KtorApiClient.getQuickAnnouncements(
                    status = "ACTIVE",
                    limit = 100,
                    offset = 0,
                    sortBy = "created_at",
                    order = "desc"
                ).map { response ->
                    response.items.orEmpty().filter { !it.message.isNullOrBlank() }
                }
            },
            updateCache = { response, fetchedAt -> putBounded(activeAnnouncementsCache, key, CachedValue(response, fetchedAt)) },
            successDetails = { announcements -> "activeAnnouncements=${announcements.size}" },
            forceRemote = forceRemote
        )
    }

    suspend fun loadParentDashboard(parentId: String, schoolId: String, forceRemote: Boolean = false): Result<ParentDashboardResponse> {
        val key = cacheKey("$parentId:$schoolId")
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadParentDashboard",
            context = "parent=$parentId school=${schoolId.ifBlank { "<any>" }}",
            cachedValue = parentDashboardCache[key],
            fetchValue = { KtorApiClient.getParentDashboard() },
            updateCache = { response, fetchedAt -> putBounded(parentDashboardCache, key, CachedValue(response, fetchedAt)) },
            successDetails = { response -> "children=${response.children?.size ?: 0} notices=${response.notices?.size ?: 0} events=${response.events?.size ?: 0}" },
            forceRemote = forceRemote
        )
    }

    suspend fun loadParentProfile(parentId: String): Result<ParentListResponse> {
        val normalizedParentId = parentId.trim()
        require(normalizedParentId.isNotBlank()) { "parentId must not be blank" }
        val key = cacheKey(normalizedParentId)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadParentProfile",
            context = "parent=$key",
            cachedValue = parentProfileCache[key],
            fetchValue = { KtorApiClient.getParentProfileWithStudents(normalizedParentId) },
            updateCache = { response, fetchedAt -> putBounded(parentProfileCache, key, CachedValue(response, fetchedAt)) },
            successDetails = { response -> "students=${response.students?.size ?: 0}" }
        )
    }

    suspend fun loadParentWards(parentId: String): Result<List<StudentResponse>> {
        val normalizedParentId = parentId.trim()
        require(normalizedParentId.isNotBlank()) { "parentId must not be blank" }
        val key = cacheKey(normalizedParentId)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadParentWards",
            context = "parent=$key",
            cachedValue = parentWardsCache[key],
            fetchValue = { KtorApiClient.getParentWards(normalizedParentId) },
            updateCache = { response, fetchedAt -> putBounded(parentWardsCache, key, CachedValue(response, fetchedAt)) },
            successDetails = { wards -> "wards=${wards.size}" }
        )
    }

    suspend fun loadParentRecord(parentId: String): Result<ParentResponse> {
        val normalizedParentId = parentId.trim()
        require(normalizedParentId.isNotBlank()) { "parentId must not be blank" }
        val key = cacheKey(normalizedParentId)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadParentRecord",
            context = "parent=$key",
            cachedValue = parentRecordCache[key],
            fetchValue = { KtorApiClient.getParentProfile(normalizedParentId) },
            updateCache = { response, fetchedAt -> putBounded(parentRecordCache, key, CachedValue(response, fetchedAt)) },
            successDetails = { response -> "name=${response.name.orEmpty().ifBlank { "<blank>" }}" }
        )
    }

    suspend fun loadTeacherDashboard(roleCode: String, forceRemote: Boolean = false): Result<TeacherDashboardOverviewResponse> {
        val key = cacheKey(roleCode)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadTeacherDashboard",
            context = "role=$key",
            cachedValue = teacherDashboardCache[key],
            fetchValue = { KtorApiClient.getTeacherDashboardOverview() },
            updateCache = { response, fetchedAt ->
                putBounded(teacherDashboardCache, key, CachedValue(response, fetchedAt))
            },
            successDetails = { response ->
                "classes=${response.myClasses.size} students=${response.totalStudents ?: 0} notices=${response.recentNotices.size} events=${response.upcomingEvents.size}"
            },
            forceRemote = forceRemote
        )
    }

    suspend fun loadDashboardAnalytics(
        roleCode: String,
        forceRemote: Boolean = false
    ): Result<DashboardAnalyticsResponse> {
        val key = cacheKey(roleCode)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadDashboardAnalytics",
            context = "role=$key",
            cachedValue = dashboardAnalyticsCache[key],
            fetchValue = { KtorApiClient.getDashboardAnalytics() },
            updateCache = { response, fetchedAt ->
                putBounded(dashboardAnalyticsCache, key, CachedValue(response, fetchedAt))
            },
            successDetails = { response -> "scope=${response.scope.ifBlank { "<blank>" }}" },
            forceRemote = forceRemote
        )
    }

    suspend fun loadDashboardSchoolsAnalytics(
        roleCode: String,
        forceRemote: Boolean = false
    ): Result<DashboardSchoolsAnalyticsResponse> {
        // Share SchoolRepository's cache so Home + Schools don't each hit /schools/analytics.
        scLog("$TAG loadDashboardSchoolsAnalytics delegate role=${cacheKey(roleCode)} forceRemote=$forceRemote")
        return com.schoolconnect.app.repository.school.SchoolRepository.loadSchoolsAnalytics(
            forceRemote = forceRemote
        )
    }

    suspend fun loadDashboardStatistics(
        roleCode: String,
        forceRemote: Boolean = false
    ): Result<DashboardStatisticsResponse> {
        val key = cacheKey(roleCode)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadDashboardStatistics",
            context = "role=$key",
            cachedValue = dashboardStatisticsCache[key],
            fetchValue = { KtorApiClient.getDashboardStatistics() },
            updateCache = { response, fetchedAt ->
                putBounded(dashboardStatisticsCache, key, CachedValue(response, fetchedAt))
            },
            successDetails = { response -> "scope=${response.scope.ifBlank { "<blank>" }}" },
            forceRemote = forceRemote
        )
    }

    suspend fun loadSchoolAnalytics(
        schoolCode: String,
        forceRemote: Boolean = false
    ): Result<SchoolAnalyticsResponse> {
        val key = cacheKey(schoolCode)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolAnalytics",
            context = "school=$key",
            cachedValue = schoolAnalyticsCache[key],
            fetchValue = { KtorApiClient.getSchoolAnalytics(key) },
            updateCache = { response, fetchedAt ->
                putBounded(schoolAnalyticsCache, key, CachedValue(response, fetchedAt))
            },
            successDetails = { response ->
                "school=${response.school.code.ifBlank { "<blank>" }} staff=${response.metrics.staff.total} students=${response.metrics.students.total} academicYears=${response.metrics.academicYears.total} subjects=${response.metrics.subjects.total}"
            },
            forceRemote = forceRemote
        )
    }

    suspend fun loadSchoolEventsNotices(
        schoolId: String,
        upcomingEventsLimit: Int = 5,
        recentNoticesLimit: Int = 5,
        forceRemote: Boolean = false
    ): Result<EventsNoticesAnalyticsResponse> {
        val key = cacheKey(schoolId)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolEventsNotices",
            context = "school=$key",
            cachedValue = schoolEventsNoticesCache[key],
            fetchValue = {
                KtorApiClient.getEventsNoticesAnalytics(
                    schoolId = key,
                    upcomingEventsLimit = upcomingEventsLimit,
                    recentNoticesLimit = recentNoticesLimit
                )
            },
            updateCache = { response, fetchedAt ->
                putBounded(schoolEventsNoticesCache, key, CachedValue(response, fetchedAt))
            },
            successDetails = { response ->
                "scope=${response.scope.ifBlank { "<blank>" }} upcoming=${response.upcomingEvents.size} notices=${response.recentNotices.size}"
            },
            forceRemote = forceRemote
        )
    }

    suspend fun loadRecentActivities(
        roleCode: String,
        forceRemote: Boolean = false
    ): Result<RecentActivitiesResponse> {
        val key = cacheKey(roleCode)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadRecentActivities",
            context = "role=$key",
            cachedValue = recentActivitiesCache[key],
            fetchValue = { KtorApiClient.getRecentActivities() },
            updateCache = { response, fetchedAt ->
                putBounded(recentActivitiesCache, key, CachedValue(response, fetchedAt))
            },
            successDetails = { response -> "count=${response.count}" },
            forceRemote = forceRemote
        )
    }

    suspend fun loadSystemHealth(
        roleCode: String,
        forceRemote: Boolean = false
    ): Result<SystemHealthResponse> {
        val key = cacheKey(roleCode)
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSystemHealth",
            context = "role=$key",
            cachedValue = systemHealthCache[key],
            fetchValue = { KtorApiClient.getSystemHealth() },
            updateCache = { response, fetchedAt ->
                putBounded(systemHealthCache, key, CachedValue(response, fetchedAt))
            },
            successDetails = { response -> "status=${response.status.ifBlank { "<blank>" }}" },
            forceRemote = forceRemote
        )
    }

    fun invalidate(roleCode: String) {
        val key = cacheKey(roleCode)
        dashboardAnalyticsCache.remove(key)
        dashboardSchoolsAnalyticsCache.remove(key)
        dashboardStatisticsCache.remove(key)
        schoolAnalyticsCache.remove(key)
        schoolEventsNoticesCache.remove(key)
        recentActivitiesCache.remove(key)
        systemHealthCache.remove(key)
        teacherDashboardCache.remove(key)
        parentDashboardCache.keys.filter { it.startsWith("$key:") }.toList().forEach(parentDashboardCache::remove)
    }

    fun invalidateAll() {
        dashboardAnalyticsCache.clear()
        dashboardSchoolsAnalyticsCache.clear()
        dashboardStatisticsCache.clear()
        schoolAnalyticsCache.clear()
        schoolEventsNoticesCache.clear()
        recentActivitiesCache.clear()
        systemHealthCache.clear()
        teacherDashboardCache.clear()
        parentDashboardCache.clear()
        parentProfileCache.clear()
        parentWardsCache.clear()
    }

    private fun cacheKey(value: String): String {
        val normalizedValue = value.trim().ifBlank { "default" }
        val userId = UserSessionStore.getUserId().trim().ifBlank { "anonymous" }
        val assignmentId = UserSessionStore.getActiveAssignmentId().trim().ifBlank { "unassigned" }
        return "$userId|$assignmentId|$normalizedValue"
    }

    private fun <T> putBounded(
        cache: MutableMap<String, CachedValue<T>>,
        key: String,
        value: CachedValue<T>
    ) {
        cache.remove(key)
        if (cache.size >= MAX_MEMORY_CACHE_ENTRIES) {
            cache.entries.firstOrNull()?.key?.let(cache::remove)
        }
        cache[key] = value
    }
}
